import { useEffect, useId, useMemo, useState } from 'react';
import { ChevronDown, Search as SearchIcon } from 'lucide-react';
import { labelsService } from '@/services/labels.service';
import { ApiError } from '@/services/http';
import { InlineLoader } from '@/components/ui/InlineLoader';
import { LABEL_TYPES, LABEL_TYPE_TITLES, type LabelType } from '@/constants/label-types';
import { cn } from '@/lib/cn';
import type { LabelView } from '@/types/api';

/**
 * CatalogFilters — loose shape that maps label types to the set of
 * selected aliases. Kept as a Record rather than seven named fields so
 * the rail can iterate uniformly; the Catalog page converts to/from
 * URLSearchParams with the same key names.
 */
export type CatalogFilters = Record<LabelType, string[]>;

export const EMPTY_FILTERS: CatalogFilters = {
  genre: [],
  topic: [],
  reading_level: [],
  interest_level: [],
  historical_event: [],
  time_period: [],
  book_series: [],
};

/**
 * FilterRail — right-column sidebar of the Catalog page per
 * docs/ADMIN_DASHBOARD.md's "Search/Filter Panel" + docs/DESIGN.md's
 * parent-mode filter rail.
 *
 * Behaviour:
 *   - Fetches the full label catalogue once on mount and buckets it by
 *     `label_type`. The server caps at ~250 aliases so in-memory is fine.
 *   - Each section is a collapsible disclosure. Exactly one opens at a
 *     time so the rail doesn't feel like a scroll-well on tall viewports.
 *   - Local "staged" state accumulates selections; `onApply` fires only
 *     when the user clicks Apply, matching the "URL is the source of
 *     truth" convention in the Catalog page.
 *   - `onClearAll` resets every section and propagates an empty filter.
 *
 * The caller passes the currently-applied `value` so the rail stays in
 * sync when the URL is the primary mutator (e.g. deep-linked Explore
 * More clicks).
 */
export interface FilterRailProps {
  value: CatalogFilters;
  onApply: (next: CatalogFilters) => void;
  /** Optional section to start with opened — used when a FilterPill is clicked. */
  initialOpenSection?: LabelType | null;
  className?: string;
}

export function FilterRail({ value, onApply, initialOpenSection, className }: FilterRailProps) {
  const [catalog, setCatalog] = useState<Record<LabelType, LabelView[]> | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  const [staged, setStaged] = useState<CatalogFilters>(value);
  const [openSection, setOpenSection] = useState<LabelType | null>(initialOpenSection ?? null);
  // Mobile-only collapse. Collapsed by default so the rail sits as a compact
  // header at the top of the page on small screens; the desktop rail ignores
  // this (it's always shown via `lg:flex`).
  const [collapsed, setCollapsed] = useState(true);
  const bodyId = useId();

  // --- Sync staged state whenever the applied value changes. ---
  useEffect(() => {
    setStaged(value);
  }, [value]);

  // --- Reflect external hints about which section should be open. ---
  useEffect(() => {
    if (initialOpenSection !== undefined && initialOpenSection !== null) {
      setOpenSection(initialOpenSection);
      // A pill was clicked to edit a filter — expand the (mobile-collapsed)
      // rail so the opened section is actually visible.
      setCollapsed(false);
    }
  }, [initialOpenSection]);

  // --- Load the full label catalogue once. ---
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const rows = await labelsService.list();
        if (cancelled) return;
        const bucket: Record<LabelType, LabelView[]> = {
          genre: [],
          topic: [],
          reading_level: [],
          interest_level: [],
          historical_event: [],
          time_period: [],
          book_series: [],
        };
        for (const label of rows) {
          bucket[label.labelType]?.push(label);
        }
        for (const type of LABEL_TYPES) {
          bucket[type].sort((a, b) => a.alias.localeCompare(b.alias));
        }
        setCatalog(bucket);
      } catch (err) {
        if (cancelled) return;
        setCatalogError(err instanceof ApiError ? err.message : 'Could not load filters.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const toggleValue = (type: LabelType, alias: string) => {
    setStaged((prev) => {
      const current = prev[type];
      const next = current.includes(alias)
        ? current.filter((v) => v !== alias)
        : [...current, alias];
      return { ...prev, [type]: next };
    });
  };

  const clearAll = () => {
    setStaged(EMPTY_FILTERS);
    onApply(EMPTY_FILTERS);
  };

  const apply = () => {
    onApply(staged);
  };

  const activeCount = useMemo(
    () => LABEL_TYPES.reduce((total, type) => total + staged[type].length, 0),
    [staged],
  );

  const hasAnyChanges = useMemo(
    () => LABEL_TYPES.some((type) => !sameSet(staged[type], value[type])),
    [staged, value],
  );

  return (
    <aside
      className={cn(
        'flex flex-col gap-4 rounded-2xl border border-or-slate-200 bg-white p-5 shadow-or-card',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="or-kicker">Browse</div>
          <h2 className="font-display text-[16px] font-bold text-or-slate-900">
            Filter books
            {activeCount > 0 ? (
              <span className="ml-2 rounded-full bg-or-orange-100 px-2 py-0.5 text-[11px] font-semibold text-or-orange-700">
                {activeCount}
              </span>
            ) : null}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          {activeCount > 0 ? (
            <button
              type="button"
              onClick={clearAll}
              className="text-[12px] font-semibold text-or-orange-600 hover:text-or-orange-700"
            >
              Clear all
            </button>
          ) : null}
          {/* Mobile-only collapse toggle — the desktop rail is always expanded. */}
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            aria-controls={bodyId}
            aria-label={collapsed ? 'Expand filters' : 'Collapse filters'}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-or-slate-600 transition hover:bg-or-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40 lg:hidden"
          >
            <ChevronDown
              className={cn('h-5 w-5 transition-transform', collapsed ? 'rotate-0' : 'rotate-180')}
              aria-hidden
            />
          </button>
        </div>
      </div>

      {/* Collapsible body: hidden by default on mobile, always shown on desktop. */}
      <div id={bodyId} className={cn('flex-col gap-4', collapsed ? 'hidden lg:flex' : 'flex')}>
        {catalogError ? (
          <p className="rounded-lg bg-or-rose-100 px-3 py-2 text-[12px] text-or-rose-700">
            {catalogError}
          </p>
        ) : null}

        <div className="flex flex-col divide-y divide-or-slate-100">
          {LABEL_TYPES.map((type) => {
            const rows = catalog?.[type] ?? [];
            const selected = staged[type];
            const isOpen = openSection === type;
            return (
              <FilterSection
                key={type}
                labelType={type}
                rows={rows}
                selected={selected}
                isOpen={isOpen}
                catalogLoaded={catalog !== null}
                onToggleOpen={() => setOpenSection(isOpen ? null : type)}
                onToggleValue={(alias) => toggleValue(type, alias)}
              />
            );
          })}
        </div>

        <button
          type="button"
          onClick={apply}
          disabled={!hasAnyChanges}
          className={cn(
            'mt-1 inline-flex h-10 items-center justify-center rounded-full text-[13px] font-semibold transition',
            'or-gradient-btn disabled:cursor-not-allowed disabled:opacity-60',
          )}
        >
          Apply filters{activeCount > 0 ? ` (${activeCount})` : ''}
        </button>
      </div>
    </aside>
  );
}

interface FilterSectionProps {
  labelType: LabelType;
  rows: LabelView[];
  selected: string[];
  isOpen: boolean;
  catalogLoaded: boolean;
  onToggleOpen: () => void;
  onToggleValue: (alias: string) => void;
}

function FilterSection({
  labelType,
  rows,
  selected,
  isOpen,
  catalogLoaded,
  onToggleOpen,
  onToggleValue,
}: FilterSectionProps) {
  const [query, setQuery] = useState('');
  const panelId = `filter-section-${labelType}`;

  const filteredRows = useMemo(() => {
    if (query.trim() === '') return rows;
    const needle = query.trim().toLowerCase();
    return rows.filter((row) => row.alias.toLowerCase().includes(needle));
  }, [rows, query]);

  return (
    <div className="py-3 first:pt-0 last:pb-0">
      <button
        type="button"
        onClick={onToggleOpen}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className="flex w-full items-center justify-between gap-2 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40"
      >
        <span className="text-[13px] font-semibold text-or-slate-900">
          {LABEL_TYPE_TITLES[labelType]}
          {selected.length > 0 ? (
            <span className="ml-2 rounded-full bg-or-orange-100 px-2 py-0.5 text-[11px] font-semibold text-or-orange-700">
              {selected.length}
            </span>
          ) : null}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-or-slate-500 transition-transform',
            isOpen ? 'rotate-180' : 'rotate-0',
          )}
          aria-hidden
        />
      </button>

      {isOpen ? (
        <div id={panelId} className="mt-3 flex flex-col gap-2">
          <div className="relative">
            <SearchIcon
              className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-or-slate-400"
              aria-hidden
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${LABEL_TYPE_TITLES[labelType].toLowerCase()}…`}
              className="h-9 w-full rounded-full border border-or-slate-200 bg-white pl-8 pr-3 text-[12px] text-or-slate-900 placeholder:text-or-slate-400 focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
            />
          </div>

          {!catalogLoaded ? (
            <InlineLoader label="Loading options…" />
          ) : filteredRows.length === 0 ? (
            <p className="text-[12px] text-or-slate-500">No matches.</p>
          ) : (
            <ul className="flex max-h-56 flex-col gap-1 overflow-y-auto pr-1">
              {filteredRows.map((row) => {
                const checked = selected.includes(row.alias);
                return (
                  <li key={row.id}>
                    <label
                      className={cn(
                        'flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] transition',
                        checked
                          ? 'bg-or-orange-50 text-or-orange-800'
                          : 'text-or-slate-700 hover:bg-or-slate-50',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => onToggleValue(row.alias)}
                        className="h-3.5 w-3.5 accent-or-orange-500"
                      />
                      <span className="flex-1 truncate">{row.alias}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** Tiny helper: are two alias lists the same set (order-insensitive)? */
function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  for (const v of b) {
    if (!setA.has(v)) return false;
  }
  return true;
}
